import * as THREE from "three";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { STLLoader } from "three/addons/loaders/STLLoader.js";
import { STLExporter } from "three/addons/exporters/STLExporter.js";
import * as BufferGeometryUtils from "three/addons/utils/BufferGeometryUtils.js";
import { getColorFromToothNumber } from "./TeethColors";
import {
  GEO_TAG,
  EXTR_TAG,
  CUT_TAG,
  TOOTH_TAG,
  STL_TAG,
  setAllModels,
  initStlViewer,
  applyBorderPoints,
  applyAnchorPoints,
  fillIntraoralList,
  viewMode,
  applyMeasurements,
} from "./IntraoralManager";

import { getMatrix4FromJsonObj } from "@/components/ortho/treatment/OrthoTreatment.js";
import {
  getGeometryFromStl,
  getGeometryFromGlb,
  getGeometryFromDrc,
  getMergedGeometry,
  getGMergedGeometry,
  exportMeshToStlBlob,
} from "@/components/geometry/geometryLoader.js";
import { load } from "laplacian-deformation";
import { hideLoader, delay } from "@/components/controls/Loader.vue";

//#region Fields

const gingivaName = "gingiva";
const gingivaNames = ["gingiva", "upper_gingiva", "lower_gingiva"];
const gingivaColor = "#ffb6c1";
const pongMaterial = new THREE.MeshPhongMaterial({
  // color: gingivaColor,
  flatShading: false,
  // flatShading: true,
  specular: 0x050505,
  shininess: 200,
  side: 2, // THREE.FrontSide,
  polygonOffset: true,
  transparent: true,
  // polygonOffsetUnit: 1,
  // wireframe: false,
});

const gingiva_geo_closed_meshes = [
  "upper_geo_closed_mesh.stl",
  "lower_geo_closed_mesh.stl",
];
const gingiva_geo_meshes = ["upper_geo_mesh.stl", "lower_geo_mesh.stl"];
const gingiva_extr_meshes = ["upper_extr_mesh.stl", "lower_extr_mesh.stl"];
const gingiva_closed_meshes = [
  "upper_extr_closed_mesh.stl",
  "lower_extr_closed_mesh.stl",
];

const addedSuffix = "added_";
const upperSuffix = "upper_";
const lowerSuffix = "lower_";

let ioUpperTransform = null;
let ioLowerTransform = null;
let ioUpperTransformInverted = null;
let ioLowerTransformInverted = null;
let ioUpperMatrix = null;
let ioUpperMatrixInverted = null;
let ioLowerMatrix = null;
let ioLowerMatrixInverted = null;
let intraoralZip = null;

//#endregion

function showLoaderPopup(text1, text2) {
  $id("loader").style.display = "block";
  $id("loader").style.color = "white";
  $("#loaderText").html(text1);
  $("#loaderText2").html(text2);
  $("#loaderText3").html(``);
}

async function processIntraoralZip(viewMode) {
  console.log("processIntraoralZip", new Date());

  console.time("intraoral download");
  let zipUrl = vueApp.intraoralZipUrl;
  zipUrl = zipUrl.replace(
    "segmentation-standalone-mobile.zip",
    "segmentation-standalone-desktop.zip",
  );
  // zipUrl = "demo/segmentation-standalone-desktop.zip";
  intraoralZip = await fetchZipAsync(zipUrl);
  if (!intraoralZip) intraoralZip = await fetchZipAsync(vueApp.intraoralZipUrl);
  console.timeEnd("intraoral download");

  if (intraoralZip) {
    showLoaderPopup(`Download transforms`, ``);
    await downloadStandaloneTransforms();

    console.time("intraoral total unzip");

    // if (vueApp.isReset)  // TODO
    let stlModels = [];
    let allKeys = Object.keys(intraoralZip.files);
    console.log("intraol zip", allKeys.length, intraoralZip);

    console.time("intraoral unzip glb");
    showLoaderPopup(`Process glb files`, ``);
    let upperBuffer = await getArrayBufferFromZip(
      intraoralZip,
      "upper_segmented.glb",
    );
    let lowerBuffer = await getArrayBufferFromZip(
      intraoralZip,
      "lower_segmented.glb",
    );
    let upperGlbModels = await processGlbBufferAsync(upperBuffer, true);
    let lowerGlbModels = await processGlbBufferAsync(lowerBuffer, false);
    let glbModels = upperGlbModels.concat(lowerGlbModels);
    console.timeEnd("intraoral unzip glb");

    let upperGingiva = glbModels.find((g) => g.isUpper);
    let lowerGingiva = glbModels.find((g) => !g.isUpper);

    if (!upperGingiva && !lowerGingiva) {
      $("#loaderText").html(`Load error. Please contact support@cephx.com`);
      return;
    }
    viewMode.isDouble = !!upperGingiva && !!lowerGingiva;

    let toothGeoKeys = allKeys.filter(
      (key) =>
        key.startsWith(GEO_TAG) &&
        key.endsWith(STL_TAG) &&
        key.includes(TOOTH_TAG),
    );
    let toothExtrKeys = allKeys.filter(
      (key) =>
        key.startsWith(EXTR_TAG) &&
        key.endsWith(STL_TAG) &&
        key.includes(TOOTH_TAG),
    );

    viewMode.hasSmoothView = viewMode.isSmoothView =
      toothGeoKeys.length > 0 && toothExtrKeys.length > 0;

    let toothKeys = toothGeoKeys.length > 0 ? toothGeoKeys : toothExtrKeys;
    if (toothKeys.length == 0) {
      toothKeys = allKeys.filter(
        (key) => key.endsWith(STL_TAG) && key.startsWith(TOOTH_TAG),
      );
    }
    console.log("intraol zip toothKeys", toothKeys.length);

    showLoaderPopup(`Process stl files`, ``);
    console.time("intraoral unzip teeth");
    for (const toothKey of toothKeys) {
      let basePath = toothKey.replace(GEO_TAG, "").replace(EXTR_TAG, "");
      let elementName = basePath.replace(STL_TAG, "");
      let model = {
        name: elementName,
        isTooth: true,
        isGingiva: false,
        isUpper:
          elementName.startsWith("tooth_1") ||
          elementName.startsWith("tooth_2"),
        filename: toothKey,
      };

      let extrPath = EXTR_TAG + basePath;
      model.extrZipFile = intraoralZip.file(extrPath);
      let cutPath = CUT_TAG + basePath;
      model.cutZipFile = intraoralZip.file(cutPath);

      model.isMaxilla = model.isUpper;
      let zipFile = intraoralZip.file(model.filename);
      let buffer = await zipFile.async("arraybuffer");
      let geometry = getGeometryFromStl(buffer);
      geometry.computeBoundingBox();
      model.mainGeometry = model.baseGeometry = model.savedGeometry = geometry;
      // }
      stlModels.push(model);
    }
    console.timeEnd("intraoral unzip teeth");

    console.time("intraoral unzip gingivae");
    let gingivaKeys = allKeys.filter((key) =>
      gingiva_geo_closed_meshes.includes(key),
    );
    if (!gingivaKeys.length)
      gingivaKeys = allKeys.filter((key) => gingiva_geo_meshes.includes(key));
    if (!gingivaKeys.length)
      gingivaKeys = allKeys.filter((key) =>
        gingiva_closed_meshes.includes(key),
      );
    if (!gingivaKeys.length)
      gingivaKeys = allKeys.filter((key) => gingiva_extr_meshes.includes(key));
    console.log("intraol zip gingivaKeys", gingivaKeys.length);

    for (const gingivaKey of gingivaKeys) {
      let zipFile = intraoralZip.file(gingivaKey);
      if (zipFile) {
        let isUpper = gingivaKey.includes(upperSuffix);
        let model = {
          name: isUpper ? "upper_gingiva" : "lower_gingiva",
          isUpper: isUpper,
          isTooth: false,
          isGingiva: true,
          filename: gingivaKey,
        };
        model.isMaxilla = model.isUpper;
        // if (model.isUpper || !viewMode.isDouble) {
        let buffer = await zipFile.async("arraybuffer");
        let geometry = getGeometryFromStl(buffer);
        geometry.computeBoundingBox();
        model.mainGeometry =
          model.baseGeometry =
          model.savedGeometry =
            geometry;
        // }

        stlModels.push(model);
      }
    }
    console.timeEnd("intraoral unzip gingivae");
    console.log("intraoral stlModels", stlModels.length);

    // let glbModels = [];
    console.log("intraoral glbModels", glbModels.length);
    console.timeEnd("intraoral total unzip");

    showLoaderPopup(`Load settings`, ``, true);

    console.time("intraoral apply jsons");

    let settings = await loadSettingsFromZip(intraoralZip);
    console.log("intraoral settings", settings);

    showLoaderPopup(`Adjust geometries`, ``, true);
    let allModels = await processZipModels(stlModels, glbModels, settings);
    console.log("intraoral allModels", allModels.length);

    setAllModels(allModels);

    showLoaderPopup(`Load edit points`, ``, true);
    if (upperGingiva) {
      await loadBorderPointsFromZip(
        intraoralZip,
        "upper_border_points.json",
        true,
      );
      await loadAnchorPointsFromZip(
        intraoralZip,
        "upper_extr_anchors.json",
        true,
      );

      await loadMeasurementsFromZip(
        intraoralZip,
        "upper_measurements.json",
        true,
      );

      let upperUrl = `${vueApp.stlBaseUrl}/saved_upper_measurements.json`;
      let jsonObj = await fetchJsonAsync(upperUrl);
      if (jsonObj) applyMeasurements(jsonObj, false, true);
    }
    if (lowerGingiva) {
      await loadBorderPointsFromZip(
        intraoralZip,
        "lower_border_points.json",
        false,
      );
      await loadAnchorPointsFromZip(
        intraoralZip,
        "lower_extr_anchors.json",
        false,
      );
      await loadMeasurementsFromZip(
        intraoralZip,
        "lower_measurements.json",
        false,
      );

      let lowerUrl = `${vueApp.stlBaseUrl}/saved_lower_measurements.json`;
      let jsonObj = await fetchJsonAsync(lowerUrl);
      if (jsonObj) applyMeasurements(jsonObj, false, false);
    }
    showLoaderPopup(`Init viewer`, `Please wait...`, true);
    initStlViewer();
  } else {
    console.log("FAIL TO GET INTRAORAL ZIP", vueApp.intraoralZipUrl);
    showLoaderPopup("Fail to get intraoral data", "");
  }

  console.timeEnd("intraoral apply jsons");
}

async function processGlbBufferAsync(buffer, isUpper) {
  let glbModels = [];
  if (buffer) {
    let gltfLoader = new GLTFLoader();
    let result = await gltfLoader.parseAsync(buffer, "");
    let world = result.scene.children[0];
    world.children.forEach((element) => {
      if (element.type === "Mesh") {
        let geometry = element.geometry;
        geometry.computeBoundingBox();
        let elementName = element.name.replace("stl", "").replace(".", "");
        let toothNumber = +elementName.replace("tooth_", "");

        let isGingiva = gingivaNames.includes(elementName);
        if (isGingiva && elementName.toLowerCase() == gingivaName) {
          elementName = isUpper
            ? `upper_${gingivaName}`
            : `lower_${gingivaName}`;
        }
        const model = {
          name: elementName,
          number: toothNumber,
          isGingiva: isGingiva,
          isUpper: isUpper,
          isMaxilla: isUpper,
          isTooth: !isGingiva,
          geometry: geometry,
          baseGeometry: geometry,
          savedGeometry: geometry,
          baseNumber: toothNumber,
          glbNumber: toothNumber,
          filename: element.name,
          isAdded: false,
        };
        // console.log(model);
        glbModels.push(model);
      }
    });
  }
  return glbModels;
}

async function processZipModels(stlModels, glbModels, settings) {
  let allModels = [];

  glbModels.forEach((glbModel) => {
    let stlModel = stlModels.find((m) => m.name == glbModel.name);
    if (stlModel) {
      if (stlModel.isTooth) {
        if (stlModel.savedGeometry)
          glbModel.savedGeometry = stlModel.savedGeometry;
        if (stlModel.mainGeometry)
          glbModel.mainGeometry = stlModel.mainGeometry;
        glbModel.cutZipFile = stlModel.cutZipFile;
        glbModel.extrZipFile = stlModel.extrZipFile;
      }
      glbModel.stlModel = stlModel;
    }
    allModels.push(glbModel);
  });

  for (const stlModel of stlModels) {
    let elementName = stlModel.name;
    let existedGlbModel = glbModels.find((m) => m.name == elementName);
    if (!existedGlbModel) {
      // isAdded
      if (!stlModel.mainGeometry) {
        let zipFile = intraoralZip.file(stlModel.filename);
        let buffer = await zipFile.async("arraybuffer");
        let geometry = getGeometryFromStl(buffer);
        geometry.computeBoundingBox();
        stlModel.mainGeometry =
          stlModel.baseGeometry =
          stlModel.savedGeometry =
            geometry;
      }

      stlModel.isAdded = true;
      allModels.push(stlModel);
      if (stlModel.isTooth) {
        console.log("intraoral added", elementName);
        stlModel.number = +elementName.replace("tooth_", "");
        stlModel.baseNumber = stlModel.number;
        stlModel.glbNumber = 0;
      }
    } else if (existedGlbModel.isGingiva) {
      existedGlbModel.filledGeometry = stlModel.mainGeometry;
    }
  }

  allModels.forEach((model) => {
    let color = gingivaColor;
    if (!model.isGingiva) {
      color = getColorFromToothNumber(model.number);
    }
    model.color = color;
    model.opacity = 0.01;
    model.isVisible = true;
    model.isDeleted = false;
    model.filename = null;

    let material = pongMaterial.clone();
    material.color = new THREE.Color(color);
    material.polygonOffsetUnit = 1;
    // https://stackoverflow.com/questions/25165013/three-js-overlapping-layers-flickering
    material.polygonOffsetFactor = model.isGingiva ? 9 : 3;

    let mesh = new THREE.Mesh(model.savedGeometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.name = model.name;

    model.mesh = mesh;
    model.mainMesh = mesh;

    if (model.filledGeometry) {
      let filledMesh = new THREE.Mesh(model.filledGeometry, material);
      filledMesh.castShadow = false;
      filledMesh.receiveShadow = false;
      filledMesh.name = model.name;
      model.filledMesh = filledMesh;
    }
  });
  applySettings(allModels, settings);
  return allModels;
}

async function loadLower(inputModels) {
  showIntraoralLoader();

  let lowerModels = inputModels.filter((m) => !m.isUpper);
  for (const model of lowerModels) {
    let stlModel = model.stlModel;
    if (stlModel && !stlModel.mainGeometry) {
      let zipFile = intraoralZip.file(stlModel.filename);
      let buffer = await zipFile.async("arraybuffer");
      let geometry = getGeometryFromStl(buffer);
      geometry.computeBoundingBox();
      stlModel.mainGeometry =
        stlModel.baseGeometry =
        stlModel.savedGeometry =
          geometry;
      if (stlModel.isTooth) {
        model.savedGeometry = stlModel.savedGeometry;

        let vmesh = model.mesh;
        vmesh.geometry.dispose();
        vmesh.geometry = model.savedGeometry.clone();
        vmesh.geometry.computeBoundingBox();

        let box = vmesh.geometry.boundingBox;
        box = offsetBox(box, 0, 0, 0);
        model.defaultBox = box;
        model.defaultPosition = {
          x: 0, // box.center.x,
          y: 0, // box.center.y,
          z: 0, // box.center.z,
        };
        // vmesh.position.set(box.center.x, box.center.y, box.center.z);

        let geometry = prepareGeometry(vmesh.geometry, model.isGingiva);
        if (vmesh.geometry != geometry) {
          vmesh.geometry.dispose();
          vmesh.geometry = geometry;
        }
      } else {
        model.filledGeometry = stlModel.geometry;
        if (model.filledGeometry) {
          let filledMesh = new THREE.Mesh(
            model.filledGeometry,
            model.mesh.material,
          );
          filledMesh.castShadow = false;
          filledMesh.receiveShadow = false;
          filledMesh.name = model.name;
          model.filledMesh = filledMesh;
        }
      }
    }
  }

  lowerMesh = createGlobalMesh(lowerMeshes, lowerGingiva);
  lowerBox = offsetBox(lowerMesh.geometry.boundingBox);
  lowerCameraCenter = new THREE.Vector3(
    lowerBox.center.x,
    lowerBox.center.y,
    lowerBox.center.z,
  );

  hideLoader();
}

async function downloadStandaloneTransforms() {
  let upperTransformUrl = `${vueApp.dicomUrl}/crown-segmentation-standalone/upper_transform.json`;
  // "https://d3lk8scmwriwsa.cloudfront.net/dicom-3190051742547063594/crown-segmentation-standalone/upper_transform.json";
  let upperTransformObj = await fetchJsonAsync(upperTransformUrl);
  ioUpperTransform = getMatrix4FromJsonObj(upperTransformObj);
  if (ioUpperTransform)
    ioUpperTransformInverted = ioUpperTransform.clone().invert();

  let lowerTransformUrl = `${vueApp.dicomUrl}/crown-segmentation-standalone/lower_transform.json`;
  // "https://d3lk8scmwriwsa.cloudfront.net/dicom-3190051742547063594/crown-segmentation-standalone/lower_transform.json";
  let lowerTransformObj = await fetchJsonAsync(lowerTransformUrl);
  ioLowerTransform = getMatrix4FromJsonObj(lowerTransformObj);
  if (ioLowerTransform)
    ioLowerTransformInverted = ioLowerTransform.clone().invert();

  if (ioLowerTransform && ioUpperTransform) {
    ioUpperMatrix = new THREE.Matrix4();
    ioUpperMatrix.multiplyMatrices(ioUpperTransformInverted, ioUpperMatrix);
    ioUpperMatrix.multiplyMatrices(ioLowerTransform, ioUpperMatrix);
    ioUpperMatrixInverted = ioUpperMatrix.clone().invert();

    ioLowerMatrix = new THREE.Matrix4();
    ioLowerMatrix.multiplyMatrices(ioLowerTransformInverted, ioLowerMatrix);
    ioLowerMatrix.multiplyMatrices(ioUpperTransform, ioLowerMatrix);
    ioLowerMatrixInverted = ioLowerMatrix.clone().invert();
  }

  console.log(
    "downloadStandaloneTransforms",
    ioUpperTransform,
    ioLowerTransform,
  );
}

async function fetchJsonAsync(jsonUrl) {
  let jsonObj = null;
  if (jsonUrl) {
    try {
      let response = await fetch(jsonUrl, { cache: "no-store" });
      if (response.status == 200) {
        jsonObj = response.json();
      } else {
        console.log(`Error response for ${jsonUrl}.`, response);
      }
    } catch (error) {
      console.log(`Error in fetchJsonAsync. Error: ${error}`);
    }
  }
  return jsonObj;
}

function fetchBinaryData(url, showPopup) {
  return new Promise((resolve, reject) => {
    if (showPopup) showLoaderPopup(`0 %`, `Download files...`);

    JSZipUtils.getBinaryContent(url, {
      progress: function (event) {
        const percent = Math.round(event.percent); //Math.ceil(event.percent * 0.99);
        if (showPopup) $("#loaderText").html(`${percent} %`);
      },
      callback: function (err, zipData) {
        if (err) {
          reject(err);
        } else {
          resolve(zipData);
        }
      },
    });
  });
}

async function fetchZipAsync(zipUrl) {
  let zip = null;
  try {
    const zipData = await fetchBinaryData(zipUrl, true);
    // console.log("fetchBinaryData", zipData);
    if (zipData) {
      showLoaderPopup(`Preparing data`, `Please wait...`, true);
      zip = await JSZip.loadAsync(zipData);
    }
    // let response = await fetch(zipUrl, { cache: "no-store" });
    // if (response.status == 200) {
    //   zip = await JSZip.loadAsync(response.arrayBuffer());
    // } else {
    //   console.log(`Error response for ${zipUrl}.`, response);
    // }
  } catch (error) {
    console.log(`Error in fetchZipAsync. Error: ${error}`);
  }
  return zip;
}

async function getArrayBufferFromZip(zip, fileName) {
  let buffer = null;
  let zipFile = zip.file(fileName);
  if (zipFile) {
    buffer = await zipFile.async("arraybuffer");
  }
  return buffer;
}

async function loadSettingsFromZip(zip) {
  let settingsObj = null;
  let fileName = "intraoral_settings.json";
  let settingsFile = zip.file(fileName);
  if (settingsFile) {
    let text = await settingsFile.async("string");
    try {
      settingsObj = JSON.parse(text);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }
  return settingsObj;
}

function applySettings(models, settings) {
  models.sort((a, b) => a.name.localeCompare(b.name));
  if (settings && settings.teeth) {
    for (let name in settings.teeth) {
      let model = models.find((m) => m.name === name);
      //getModelByName(name);
      if (model) {
        let toothSettings = settings.teeth[name];
        if (
          typeof toothSettings.isAdded !== "undefined" &&
          toothSettings.isAdded
        )
          model.isAdded = toothSettings.isAdded;

        if (typeof toothSettings.glbNumber !== "undefined") {
          model.glbNumber = settings.teeth[name].glbNumber;
          if (model.glbNumber && !model.isAdded) {
            model.isAdded = false;
          }
        }

        model.isSaved = settings.teeth[name].isSaved;
        model.isDeleted = model.isDeleted || settings.teeth[name].isDeleted;

        if (typeof toothSettings.isUpper !== "undefined")
          model.isUpper = model.isMaxilla = toothSettings.isUpper;
      }
    }

    // OBSOLETE ?
    let settingsKeys = Object.keys(settings.teeth);
    console.log("settingsKeys", settingsKeys);
    models
      .map((x) => x)
      .forEach((model) => {
        let settingsIndex = settingsKeys.indexOf(model.name);
        if (model.isTooth && settingsIndex < 0) {
          model.isDeleted = true;
          // let modelIndex = models.indexOf(model);
          //  models.splice(modelIndex, 1);
        }
      });
  }
}

async function loadBorderPointsFromZip(zip, fileName, isUpper) {
  let basePointsFile = zip.file(fileName);
  // console.log("loadBorderPointsFromZip", fileName, basePointsFile);
  if (basePointsFile) {
    let text = await basePointsFile.async("string");
    try {
      let borderPoints = JSON.parse(text);
      applyBorderPoints(borderPoints, true, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  } else {
    console.log(`No such file in zip response - ${fileName}.`);
  }

  let savedPointsFile = zip.file("saved_" + fileName);
  // console.log("savedPointsFile", savedPointsFile);
  if (savedPointsFile && !vueApp.isReset) {
    let text = await savedPointsFile.async("string");
    try {
      let borderPoints = JSON.parse(text);
      applyBorderPoints(borderPoints, false, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${savedPointsFile}! Error: ${e}`);
    }
  }
}

async function loadAnchorPointsFromZip(zip, fileName, isUpper) {
  let baseFile = zip.file(fileName);
  // console.log("loadAnchorPointsFromZip", fileName, baseAnchorsFile);
  if (baseFile) {
    let text = await baseFile.async("string");
    try {
      let anchorPoints = JSON.parse(text);
      applyAnchorPoints(anchorPoints, true, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }

  let savedName = "saved_" + fileName;
  let savedAnchorsFile = zip.file(savedName);
  // console.log("savedAnchorsFile", savedName, savedAnchorsFile);
  if (savedAnchorsFile && !vueApp.isReset) {
    let text = await savedAnchorsFile.async("string");
    try {
      let anchorPoints = JSON.parse(text);
      applyAnchorPoints(anchorPoints, false, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }
}

async function loadMeasurementsFromZip(zip, fileName, isUpper) {
  let baseFile = zip.file(fileName);
  if (baseFile) {
    let text = await baseFile.async("string");
    try {
      let measurements = JSON.parse(text);
      applyMeasurements(measurements, true, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }

  let savedName = "saved_" + fileName;
  let savedFile = zip.file(savedName);
  // console.log("loadMeasurementsFromZip", baseFile, savedFile);
  if (savedFile && !vueApp.isReset) {
    let text = await savedFile.async("string");
    try {
      let measurements = JSON.parse(text);
      applyMeasurements(measurements, false, isUpper);
    } catch (e) {
      console.log(`Invalid JSON: ${fileName}! Error: ${e}`);
    }
  }
}

//#region  Obsolete

async function fetchZip2(callback) {
  var promise = new JSZip.external.Promise(function (resolve, reject) {
    JSZipUtils.getBinaryContent(vueApp.intraoralZipUrl, {
      progress: function (event) {
        let percent = Math.ceil(event.percent * 0.99);
        console.log(`intraoral zip ${percent} %`);
      },
      callback: function (err, zipData) {
        if (err) {
          console.log(err);
        } else {
          console.log(`intraoral zip downloaded, ${vueApp.intraoralZipUrl}`);
          resolve(zipData);
          // callback(zipData);
        }
      },
    });
  });

  promise
    .then(JSZip.loadAsync)
    .then(function (zip) {
      callback(zip);
    })
    .then(
      function success(text) {
        console.log("intraoral zip success");
      },
      function error(e) {
        if (callback) callback(null);
      },
    );
}

async function fetchGlbBuffer(glbUrl, callback) {
  if (!glbUrl) {
    callback(null);
    return;
  }
  fetch(glbUrl)
    .then(async (response) => {
      // console.log(response);
      if (response.status == 200) {
        let buffer = await response.arrayBuffer();
        callback(buffer);
      } else callback(null);
    })
    .catch((error) => {
      console.log(`Error response for ${glbUrl}. Error: ${error}`);
      callback(null);
    });
}

async function fetchPointsJson(jsonUrl) {
  if (!jsonUrl) return;
  fetch(jsonUrl)
    .then((response) => {
      response.text().then((text) => {
        try {
          let borderPoints = JSON.parse(text);
          applyBorderPoints(borderPoints);
        } catch (e) {
          console.log(`Invalid JSON: ${jsonUrl}! Error: ${e}`);
        }
      });
    })
    .catch((error) => {
      console.log(`Error response for ${jsonUrl}. Error: ${error}`);
    });
}

//#endregion

export {
  intraoralZip,
  processIntraoralZip,
  getGeometryFromStl,
  getGeometryFromGlb,
  getGeometryFromDrc,
  getMergedGeometry,
  getGMergedGeometry,
  exportMeshToStlBlob,
  ioUpperMatrix,
  ioUpperMatrixInverted,
  ioLowerMatrix,
  ioLowerMatrixInverted,
  fetchJsonAsync,
  fetchZipAsync,
  loadMeasurementsFromZip,
  loadLower,
};
